import pickBy from 'lodash/pickBy';
import isEmpty from 'lodash/isEmpty';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { types as sdkTypes, createImageVariantConfig } from '../../util/sdkLoader';
import {
  bookingTimeUnits,
  findNextBoundary,
  getStartOf,
  monthIdString,
  stringifyDateToISO8601,
} from '../../util/dates';
import { isTransactionsTransitionInvalidTransition, storableError } from '../../util/errors';
import { initiateOrder, confirmPayment } from '../CheckoutPage/CheckoutPage.duck';
import { confirmCardPayment } from '../../ducks/stripe.duck';
import { declineAllUserBidsAPI, getTransaction, onGetEventBussinessListing, sendEmailToAdmin, transactionLineItems, transitionPrivileged, updateListingForPartialPaymentTransaction, updateTransactionMetadata } from '../../util/api';
import * as log from '../../util/log';
import {
  updatedEntities,
  denormalisedEntities,
  denormalisedResponseEntities,
} from '../../util/data';
import {
  resolveLatestProcessName,
  getProcess,
  isBookingProcess,
} from '../../transactions/transaction';

import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUser, fetchCurrentUserNotifications } from '../../ducks/user.duck';
import { showListing } from '../ListingPage/ListingPage.duck';

const { UUID } = sdkTypes;

const MESSAGES_PAGE_SIZE = 100;
const REVIEW_TX_INCLUDES = ['reviews', 'reviews.author', 'reviews.subject'];
const MINUTE_IN_MS = 1000 * 60;

// Day-based time slots queries are cached for 1 minute.
const removeOutdatedDateData = timeSlotsForDate => {
  const now = new Date().getTime();
  const minuteAgo = now - MINUTE_IN_MS;
  return Object.fromEntries(
    Object.entries(timeSlotsForDate).filter(([dateId, data]) => {
      return data.fetchedAt && data.fetchedAt > minuteAgo;
    })
  );
};

// Helper to fetch correct image variants for different thunk calls
const getImageVariants = listingImageConfig => {
  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } = listingImageConfig;
  const aspectRatio = aspectHeight / aspectWidth;
  return {
    'fields.image': [
      // Profile images
      'variants.square-small',
      'variants.square-small2x',

      // Listing images:
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
  };
};

const delay = ms => new Promise(resolve => window.setTimeout(resolve, ms));
const refreshTx = (sdk, txId) => sdk.transactions.show({ id: txId }, { expand: true });
const refreshTransactionEntity = (sdk, txId, dispatch) => {
  delay(3000)
    .then(() => refreshTx(sdk, txId))
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      const lastTransition = response?.data?.data?.attributes?.lastTransition;
      // We'll make another attempt if mark-received-from-purchased from default-purchase process is still the latest.
      if (lastTransition === 'transition/mark-received-from-purchased') {
        return delay(8000)
          .then(() => refreshTx(sdk, txId))
          .then(response => {
            dispatch(addMarketplaceEntities(response));
          });
      }
    })
    .catch(e => {
      // refresh failed, but we don't act upon it.
      console.log('error', e);
    });
};

// ================ Async Thunks ================ //

////////////////////
// fetchTimeSlots //
////////////////////
const fetchTimeSlotsPayloadCreator = (
  { listingId, start, end, timeZone, options },
  { rejectWithValue, extra: sdk, getState }
) => {
  const { extraQueryParams = null, useFetchTimeSlotsForDate = false } = options || {};

  // The maximum pagination page size for timeSlots is 500
  const extraParams = extraQueryParams || {
    perPage: 500,
    page: 1,
  };

  // For small time units, we fetch the data per date.
  // This is to avoid fetching too much data (with 15 minute intervals, there can be 24*4*31 = 2928 time slots)
  if (useFetchTimeSlotsForDate) {
    const dateId = stringifyDateToISO8601(start, timeZone);
    const dateData = getState().TransactionPage.timeSlotsForDate[dateId];
    const minuteAgo = new Date().getTime() - MINUTE_IN_MS;
    const hasRecentlyFetchedData = dateData?.fetchedAt > minuteAgo;
    if (hasRecentlyFetchedData) {
      return Promise.resolve({ timeSlots: dateData?.timeSlots || [], dateId, type: 'date' });
    }

    return sdk.timeslots
      .query({ listingId, start, end, ...extraParams })
      .then(response => {
        const timeSlots = denormalisedResponseEntities(response);
        return { timeSlots, dateId, type: 'date' };
      })
      .catch(e => {
        return rejectWithValue({ error: storableError(e), dateId, type: 'date' });
      });
  } else {
    const monthId = monthIdString(start, timeZone);
    return sdk.timeslots
      .query({ listingId, start, end, ...extraParams })
      .then(response => {
        const timeSlots = denormalisedResponseEntities(response);
        return { timeSlots, monthId, type: 'month' };
      })
      .catch(e => {
        return rejectWithValue({ error: storableError(e), monthId, type: 'month' });
      });
  }
};

export const fetchTimeSlotsThunk = createAsyncThunk(
  'TransactionPage/fetchTimeSlots',
  fetchTimeSlotsPayloadCreator
);

// Backward compatible wrapper for fetchTimeSlots
export const fetchTimeSlots = (listingId, start, end, timeZone, options) => dispatch => {
  return dispatch(fetchTimeSlotsThunk({ listingId, start, end, timeZone, options }));
};

//////////////////////
// fetchTransaction //
//////////////////////

// Helper function for loadData call.
const fetchMonthlyTimeSlots = (dispatch, listing) => {
  const hasWindow = typeof window !== 'undefined';
  const { availabilityPlan, publicData } = listing?.attributes || {};
  const tz = availabilityPlan?.timezone;

  // Fetch time-zones on client side only.
  if (hasWindow && listing.id && !!tz) {
    const { unitType, priceVariants, startTimeInterval } = publicData || {};
    const now = new Date();
    const startOfToday = getStartOf(now, 'day', tz);
    const isFixed = unitType === 'fixed';

    const timeUnit = startTimeInterval
      ? bookingTimeUnits[startTimeInterval]?.timeUnit
      : unitType === 'hour'
        ? 'hour'
        : 'day';
    const nextBoundary = findNextBoundary(now, 1, timeUnit, tz);

    const nextMonth = getStartOf(nextBoundary, 'month', tz, 1, 'months');
    const nextAfterNextMonth = getStartOf(nextMonth, 'month', tz, 1, 'months');

    const variants = priceVariants || [];
    const bookingLengthInMinutes = variants.reduce((min, priceVariant) => {
      return Math.min(min, priceVariant.bookingLengthInMinutes);
    }, Number.MAX_SAFE_INTEGER);

    const nextMonthEnd = isFixed
      ? getStartOf(nextMonth, 'minute', tz, bookingLengthInMinutes, 'minutes')
      : nextMonth;
    const followingMonthEnd = isFixed
      ? getStartOf(nextAfterNextMonth, 'minute', tz, bookingLengthInMinutes, 'minutes')
      : nextAfterNextMonth;

    const minDurationStartingInInterval = isFixed ? bookingLengthInMinutes : 60;

    const options = intervalAlign => {
      return ['fixed', 'hour'].includes(unitType)
        ? {
          extraQueryParams: {
            intervalDuration: 'P1D',
            intervalAlign,
            maxPerInterval: 1,
            minDurationStartingInInterval,
            perPage: 31,
            page: 1,
          },
        }
        : null;
    };

    return Promise.all([
      dispatch(
        fetchTimeSlotsThunk({
          listingId: listing.id,
          start: nextBoundary,
          end: nextMonthEnd,
          timeZone: tz,
          options: options(startOfToday),
        })
      ),
      dispatch(
        fetchTimeSlotsThunk({
          listingId: listing.id,
          start: nextMonth,
          end: followingMonthEnd,
          timeZone: tz,
          options: options(nextMonth),
        })
      ),
    ]);
  }

  // By default return an empty array
  return Promise.all([]);
};

// fetchTransaction
const fetchTransactionPayloadCreator = (
  { id, txRole, config },
  { dispatch, rejectWithValue, extra: sdk }
) => {
  const listingRelationship = txResponse => {
    return txResponse.data.data.relationships.listing.data;
  };
  let txResponse = null;
  let businessListing = false;
  let eventListing = false;


  // sdk.assetsByAlias({ paths: ['transactions/commission.json'], alias: 'latest' })
  //   .then((responese) => {
  //     dispatch(fetchCommissionSuccess(responese.data));
  //   })
  //   .catch(err => {
  //     console.log(err);
  //     dispatch(fetchCommissionError(storableError(err)))
  //   });

  return (txRole == 'admin'
    ? getTransaction({
      id: id.uuid,
      include: [
        'customer',
        'customer.profileImage',
        'provider',
        'provider.profileImage',
        'listing',
        'listing.currentStock',
        'booking',
        'reviews',
        'reviews.author',
        'reviews.subject',
      ],
      ...getImageVariants(config.layout.listingImage),
    })
    : sdk.transactions.show(
      {
        id,
        include: [
          'customer',
          'customer.profileImage',
          'provider',
          'provider.profileImage',
          'listing',
          'listing.currentStock',
          'listing.images',
          'listing.author',
          'listing.author.profileImage',
          'booking',
          'reviews',
          'reviews.author',
          'reviews.subject',
        ],
        ...getImageVariants(config.layout.listingImage),
      },
      { expand: true }
    ))
    .then(async response => {
      txResponse = response;
      const listingId = listingRelationship(response).id;
      const entities = updatedEntities({}, response.data);
      const listingRef = { id: listingId, type: 'listing' };
      const transactionRef = { id, type: 'transaction' };
      const denormalised = denormalisedEntities(entities, [listingRef, transactionRef]);
      const listing = denormalised[0];
      const transaction = denormalised[1];

      const processName = resolveLatestProcessName(transaction.attributes.processName);

      const { isBiddingFlow = false, isPaymentFlow = false, businessListingId, inquiryListingId } = (transaction && transaction.id && transaction.attributes.protectedData) || {};
      if (isBiddingFlow && (transaction.attributes.lastTransition === "transition/inquire" ||
        transaction.attributes.lastTransition === 'transition/provider-final-accept')
      ) {
        const orderData = {
          stockReservationQuantity: 1,
          seats: 1,
          protectedData: {
            ...transaction.attributes.protectedData,
          }
        }
        dispatch(fetchTransactionLineItems({
          orderData,
          listingId,
          isOwnListing: false,
        }));
      }

      if (businessListingId) {
        dispatch(fetchBusinessListing(businessListingId, config, false));
      }

      // For payment flow, fetch the full inquiry (event) listing — the booked
      // transaction view renders the RFP breakdown from it.
      const isPaymentProcess =
        isPaymentFlow ||
        processName === 'default-bidpayment' ||
        processName === 'default-accept-bid';
      if (isPaymentProcess && inquiryListingId) {
        try {
          const inquiryListingResponse = await sdk.listings.show({
            id: inquiryListingId,
            include: ['images'],
            'fields.image': [
              'variants.scaled-medium',
              'variants.listing-card',
              'variants.square-small2x',
            ],
          });
          const inquiryListingEntity =
            denormalisedResponseEntities(inquiryListingResponse)?.[0] || null;
          if (inquiryListingEntity) {
            dispatch(setInquiryListingEntity(inquiryListingEntity));
          }
          const defaultImage =
            inquiryListingEntity?.attributes?.publicData?.defaultImage || null;
          if (defaultImage) {
            dispatch(setInquiryListingDefaultImage(defaultImage));
          }
        } catch (e) {
          console.error('Failed to fetch inquiry listing', e);
        }
      }

      // const { businessListingId, eventListingId } = transaction.attributes.protectedData || {};
      // const getEventBussinessListing = await onGetEventBussinessListing({ businessListingId, eventListingId });
      // const getEventBussinessListing = await onGetEventBussinessListing({ businessListingId, listingId });
      // eventListing = getEventBussinessListing.eventListing
      // businessListing = getEventBussinessListing.businessListing;

      try {
        const process = getProcess(processName);
        const isInquiry = process.getState(transaction) === process.states.INQUIRY;

        // Fetch time slots for transactions that are in inquired state
        const canFetchTimeslots =
          txRole === 'customer' && isBookingProcess(processName) && isInquiry;

        if (canFetchTimeslots) {
          fetchMonthlyTimeSlots(dispatch, listing);
        }
      } catch (error) {
        console.log(`transaction process (${processName}) was not recognized`);
      }

      const restPaymentTxId = transaction?.attributes?.metadata?.restPaymentTxId;
      if (restPaymentTxId) {
        try {
          const restTxResponse = await sdk.transactions.show(
            { id: new UUID(restPaymentTxId), include: ['customer', 'provider', 'listing'] },
            { expand: true }
          );
          dispatch(addMarketplaceEntities(restTxResponse));
        } catch (e) {
          console.error('Failed to fetch rest payment transaction', e);
        }
      }

      // Fetch linked invoice (negotiation) transactions so their transitions
      // can be merged into the ActivityFeed.
      const invoiceTxIds = transaction?.attributes?.metadata?.invoiceTxIds || [];
      const fetchedInvoiceTxIds = [];
      for (const invoiceTxId of invoiceTxIds) {
        try {
          const invoiceTxResponse = await sdk.transactions.show(
            { id: new UUID(invoiceTxId), include: ['customer', 'provider', 'listing'] },
            { expand: true }
          );
          dispatch(addMarketplaceEntities(invoiceTxResponse));
          fetchedInvoiceTxIds.push(invoiceTxId);
        } catch (e) {
          console.warn('Failed to fetch invoice transaction', invoiceTxId, e?.message);
        }
      }

      return { ...response, restPaymentTxId, invoiceTxIds: fetchedInvoiceTxIds };
    })
    .then(response => {
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };

      dispatch(addMarketplaceEntities(txResponse, sanitizeConfig));
      dispatch(addMarketplaceEntities(response, sanitizeConfig));
      // dispatch(fetchTransactionSuccess({ txResponse: txResponse, eventListing: eventListing, businessListing: businessListing }));
      return response;
    })
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const fetchTransactionThunk = createAsyncThunk(
  'TransactionPage/fetchTransaction',
  fetchTransactionPayloadCreator
);

// Backward compatible wrapper for fetchTransaction
export const fetchTransaction = (id, txRole, config) => dispatch => {
  return dispatch(fetchTransactionThunk({ id, txRole, config }));
};

//////////////////////////
// fetchBusinessListing //
/////////////////////////
const fetchBusinessListingPayloadCreator = (
  { id, config, isOwn },
  { dispatch, rejectWithValue, extra: sdk }
) => {
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  // Current user entity is fetched in a bit lazy fashion, since it's not tied to returned Promise chain.
  const fetchCurrentUserOptions = {
    updateHasListings: false,
    updateNotifications: false,
  };
  dispatch(fetchCurrentUser(fetchCurrentUserOptions));

  const params = {
    id,
    include: ['author', 'author.profileImage', 'images', 'currentStock'],
    'fields.image': [
      // Scaled variants for large images
      'variants.scaled-small',
      'variants.scaled-medium',
      'variants.scaled-large',
      'variants.scaled-xlarge',

      // Cropped variants for listing thumbnail images
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
      `variants.${variantPrefix}-4x`,
      `variants.${variantPrefix}-6x`,

      // Social media
      'variants.facebook',
      'variants.twitter',

      // Avatars
      'variants.square-small',
      'variants.square-small2x',
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-4x`, 1600, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-6x`, 2400, aspectRatio),
  };

  const show = isOwn ? sdk.ownListings.show(params) : sdk.listings.show(params);

  return show
    .then(data => {
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };
      dispatch(addMarketplaceEntities(data, sanitizeConfig));
      return data;
    })
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const fetchBusinessListingThunk = createAsyncThunk(
  'TransactionPage/fetchBusinessListing',
  fetchBusinessListingPayloadCreator
);

// Backward compatible wrapper for fetchTransaction
export const fetchBusinessListing = (id, config, isOwn) => dispatch => {
  return dispatch(fetchBusinessListingThunk({ id, config, isOwn }));
};

////////////////////////////////////
// fetchBusinessListingByAuthor  //
///////////////////////////////////
// Bid transactions do not all carry businessListingId in protectedData: the
// RFP bid flow only writes it when the bidder's own profile happens to hold
// one, so bids from providers without it produce a transaction the organiser
// cannot check out against. Resolve the listing from its author instead —
// a business listing is public, and a provider has exactly one.
const fetchBusinessListingByAuthorPayloadCreator = (
  { authorId, config },
  { dispatch, rejectWithValue, extra: sdk }
) => {
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  const params = {
    authorId,
    include: ['author', 'author.profileImage', 'images', 'currentStock'],
    'fields.image': [
      'variants.scaled-small',
      'variants.scaled-medium',
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
      'variants.square-small',
      'variants.square-small2x',
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    perPage: 100,
  };

  return sdk.listings
    .query(params)
    .then(data => {
      const listingFields = config?.listing?.listingFields;
      dispatch(addMarketplaceEntities(data, { listingFields }));
      return data;
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const fetchBusinessListingByAuthorThunk = createAsyncThunk(
  'TransactionPage/fetchBusinessListingByAuthor',
  fetchBusinessListingByAuthorPayloadCreator
);

export const fetchBusinessListingByAuthor = (authorId, config) => dispatch => {
  return dispatch(fetchBusinessListingByAuthorThunk({ authorId, config }));
};

//////////////////////////
// fetchInquiryListing //
/////////////////////////
const fetchInquiryListingPayloadCreator = (
  { id, config },
  { dispatch, rejectWithValue, extra: sdk }
) => {
  const params = {
    id,
    include: ['author', 'images'],
    'fields.image': ['variants.square-small', 'variants.square-small2x'],
  };

  return sdk.listings
    .show(params)
    .then(data => {
      return denormalisedEntities(data, [data.data])[0];
    })
    .catch(e => {
      // Don't reject immediately, let the caller handle errors
      return rejectWithValue(storableError(e));
    });
};

export const fetchInquiryListingThunk = createAsyncThunk(
  'TransactionPage/fetchInquiryListing',
  fetchInquiryListingPayloadCreator
);

// Backward compatible wrapper for fetchInquiryListing
export const fetchInquiryListing = (id, config) => dispatch => {
  return dispatch(fetchInquiryListingThunk({ id, config }));
};

// Resolve the RFP (event) listing through a linked transaction — used when a
// payment-flow transaction doesn't carry inquiryListingId in protectedData.
const fetchLinkedTransactionListingPayloadCreator = (
  { transactionId },
  { rejectWithValue, extra: sdk }
) => {
  return sdk.transactions
    .show({
      id: transactionId,
      include: ['listing', 'listing.images'],
      'fields.image': ['variants.scaled-medium', 'variants.square-small2x'],
    })
    .then(response => {
      const [tx] = denormalisedEntities(response, [response.data]);
      return tx?.listing || null;
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const fetchLinkedTransactionListingThunk = createAsyncThunk(
  'TransactionPage/fetchLinkedTransactionListing',
  fetchLinkedTransactionListingPayloadCreator
);

export const fetchLinkedTransactionListing = transactionId => dispatch => {
  return dispatch(fetchLinkedTransactionListingThunk({ transactionId }));
};

/////////////////////
// fetchCommission //
/////////////////////
const fetchCommissionPayloadCreator = (arg, { rejectWithValue, extra: sdk }) => {
  return sdk
    .assetsByAlias({
      paths: ['transactions/commission.json'],
      alias: 'latest',
    })
    .then(response => response.data)
    .catch(e => rejectWithValue(storableError(e)));
};

export const fetchCommissionThunk = createAsyncThunk(
  'TransactionPage/fetchCommission',
  fetchCommissionPayloadCreator
);

// Backward compatible wrapper for fetchCommission
export const fetchCommission = () => dispatch => {
  return dispatch(fetchCommissionThunk());
};


////////////////////
// makeTransition //
////////////////////
const makeTransitionPayloadCreator = (
  { txId, transitionName, params },
  { dispatch, rejectWithValue, extra: sdk, getState }
) => {
  const transaction = getState()?.marketplaceData?.entities?.transaction?.[txId?.uuid] || {};
  const processName = resolveLatestProcessName(transaction?.attributes?.processName) || null;
  const process = getProcess(processName);
  const flowProtectedData = {
    ...(params?.orderData?.isBiddingFlow ? { isBiddingFlow: true } : {}),
    ...(params?.orderData?.isPaymentFlow ? { isPaymentFlow: true } : {}),
    ...(params?.orderData?.isNegotiationFlow ? { isNegotiationFlow: true } : {}),
  };

  // This calls the client app's server to make a privileged transition.
  const privilegedTransition = () =>
    // Server expects flow flags under bodyParams.params.protectedData (or at top-level of params)
    // to derive listing unitType & process alias before calculating line items.
    transitionPrivileged({
      isSpeculative: false,
      orderData: params?.orderData || {},
      bodyParams: {
        id: txId,
        transition: transitionName,
        params: {
          protectedData: flowProtectedData,
        }, // NOTE: lineItems and metadata are included on the server-side.
      },
      queryParams: {
        expand: true,
      },
    });
  const normalTransition = () =>
    sdk.transactions.transition({ id: txId, transition: transitionName, params }, { expand: true });
  const makeCall = process?.isPrivileged(transitionName) ? privilegedTransition : normalTransition;

  return makeCall()
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(fetchCurrentUserNotifications());

      // There could be automatic transitions after this transition
      // For example mark-received-from-purchased > auto-complete.
      // Here, we make 1-2 delayed updates for the tx entity.
      // This way "leave a review" link should show up for the customer.
      refreshTransactionEntity(sdk, txId, dispatch);

      return response;
    })
    .catch(e => {
      log.error(e, `${transitionName}-failed`, {
        txId,
        transition: transitionName,
      });
      return rejectWithValue(storableError(e));
    });
};

export const makeTransitionThunk = createAsyncThunk(
  'TransactionPage/makeTransition',
  makeTransitionPayloadCreator,
  {
    condition: ({ txId, transitionName, params }, { getState }) => {
      const state = getState();
      if (state.TransactionPage.transitionInProgress) {
        return false; // Don't execute the thunk if transition is already in progress
      }
      return true; // Execute the thunk
    },
  }
);

// Backward compatible wrapper for makeTransition
export const makeTransition = (txId, transitionName, params) => dispatch => {
  return dispatch(makeTransitionThunk({ txId, transitionName, params }));
};

////////////////////////////
// makeTransition Normal //
//////////////////////////
const makeTransitionPayloadCreatorNormal = (
  { txId, transitionName, params },
  { dispatch, rejectWithValue, extra: sdk, getState }
) => {

  const normalTransition = () =>
    sdk.transactions.transition({ id: txId, transition: transitionName, params }, { expand: true });

  return normalTransition()
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(fetchCurrentUserNotifications());

      // There could be automatic transitions after this transition
      // For example mark-received-from-purchased > auto-complete.
      // Here, we make 1-2 delayed updates for the tx entity.
      // This way "leave a review" link should show up for the customer.
      refreshTransactionEntity(sdk, txId, dispatch);

      return response;
    })
    .catch(e => {
      log.error(e, `${transitionName}-failed`, {
        txId,
        transition: transitionName,
      });
      return rejectWithValue(storableError(e));
    });
};

export const makeTransitionNormalThunk = createAsyncThunk(
  'TransactionPage/makeTransitionNormal',
  makeTransitionPayloadCreatorNormal,
);

// Backward compatible wrapper for makeTransition
export const makeTransitionNormal = (txId, transitionName, params) => dispatch => {
  return dispatch(makeTransitionNormalThunk({ txId, transitionName, params }));
};

////////////////////////////
// decline all user bids //
//////////////////////////
// The server no longer accepts `services` from the client: which bids get
// closed is derived from the accepted transaction's own selectedServices, so a
// caller cannot widen or narrow it. `orderId` is passed because the winner's
// notification links to /sale/<orderId> — on the bid transaction the bidder is
// the customer, so linking to that one would show them the organiser's side.
const declineAllUserBidsPayloadCreator = (
  { listingId, acceptedTransactionId, orderId, serviceProviderId },
  { dispatch, rejectWithValue, extra: sdk, getState }
) => {
  const body = {
    listingId,
    acceptedTransactionId,
    orderId,
    serviceProviderId,
  };

  return declineAllUserBidsAPI(body);
};

export const declineAllUserBidsThunk = createAsyncThunk(
  'TransactionPage/declineAllUserBids',
  declineAllUserBidsPayloadCreator,
);

// Takes an options object rather than positional arguments: the previous
// signature put listingId and two ids in a row, which is easy to transpose.
export const declineAllUserBids = ({
  listingId,
  acceptedTransactionId,
  orderId,
  serviceProviderId,
} = {}) => dispatch => {
  return dispatch(
    declineAllUserBidsThunk({ listingId, acceptedTransactionId, orderId, serviceProviderId })
  );
};

/////////////////////////////
// Initiate Rest Payment  //
///////////////////////////
/**
 * Initiates the remaining 75% payment for a transaction that had a 25% partial payment.
 * This replicates the checkout flow (initiate order → Stripe confirmCardPayment → confirm payment)
 * but uses the customer's saved card, so no redirect to CheckoutPage is needed.
 *
 * After a successful payment:
 * - The new 75% transaction ID is stored in the original 25% transaction's protectedData as `restPaymentTxId`
 * - The listing's publicData is updated with `restPaymentTxId`
 *
 * @param {Object} params
 * @param {Object} params.transaction - The original 25% transaction entity
 * @param {Object} params.listing - The listing entity associated with the transaction
 * @param {Object} params.currentUser - The current user (customer) with stripeCustomer
 * @param {Object} params.config - App-wide config
 */
const initiateRestPaymentPayloadCreator = (
  { transaction, listing, currentUser, config },
  { dispatch, rejectWithValue, extra: sdk, getState }
) => {
  // ── Step 0: Fetch Stripe customer data (not available on TransactionPage) ──
  const fetchCurrentUserOptions = {
    callParams: { include: ['stripeCustomer.defaultPaymentMethod'] },
    updateHasListings: false,
    updateNotifications: false,
    enforce: true,
  };

  return dispatch(fetchCurrentUser(fetchCurrentUserOptions))
    .then(() => {
      // Read the refreshed currentUser from Redux state
      const freshCurrentUser = getState()?.user?.currentUser;
      const stripeCustomer = freshCurrentUser?.stripeCustomer;
      const defaultPaymentMethod = stripeCustomer?.defaultPaymentMethod;
      const stripePaymentMethodId = defaultPaymentMethod?.attributes?.stripePaymentMethodId;

      if (!stripePaymentMethodId) {
        throw new Error('No saved payment method found. Please add a card from the payment methods page.');
      }

      return stripePaymentMethodId;
    })
    .then(stripePaymentMethodId => {
      // ── Validate prerequisites ──────────────────────────────────────────
      if (!transaction?.id) {
        throw new Error('Transaction not found.');
      }

      if (!listing?.id) {
        throw new Error('Listing not found.');
      }

      const processAlias = 'default-rest-payment/release-1';

      const processName = resolveLatestProcessName(
        processAlias.split('/')[0]
      );
      let process;
      try {
        process = getProcess(processName);
      } catch (err) {
        throw err;
      }

      const { listingType, unitType } = listing.attributes?.publicData || {};
      const listingTypeConfig = config.listing.listingTypes.find(lt => lt.listingType === listingType);
      const transactionTypeData = listingTypeConfig?.transactionType
        ? { unitType: unitType || listingTypeConfig.transactionType.unitType }
        : {};

      // ── Build order params ──────────────────────────────────────────────
      const originalProtectedData = transaction.attributes?.protectedData || {};

      const reductionAmountInSubunits = transaction.attributes?.metadata?.reductionAmountInSubunits || 0;

      const orderParams = {
        listingId: listing.id.uuid,
        quantity: 1,
        paymentMethod: stripePaymentMethodId,
        protectedData: {
          ...originalProtectedData,
          ...transactionTypeData,
          currency: config.currency,
          paymentPercentage: 75,
          isPaymentFlow: true,
          isRestPaymentFlow: true,
          originalTransactionId: transaction.id.uuid,
          reductionAmountInSubunits,
          quantity: 1,
          seats: 1,
        },
      };

      const requestTransition = process.transitions.REQUEST_PAYMENT;
      if (!requestTransition) {
        throw new Error('Missing REQUEST_PAYMENT transition for the current process.');
      }
      const isPrivileged = process.isPrivileged(requestTransition);

      // ── Step 1: Initiate order ──────────────────────────────────────────
      return dispatch(initiateOrder(orderParams, processAlias, null, requestTransition, isPrivileged))
        .then(order => {
          // ── Step 2: Confirm card payment via Stripe ───────────────────
          const stripePaymentIntents = order?.attributes?.protectedData?.stripePaymentIntents;
          if (!stripePaymentIntents?.default?.stripePaymentIntentClientSecret) {
            throw new Error('Missing Stripe PaymentIntent client secret from initiated order.');
          }

          const { stripePaymentIntentClientSecret } = stripePaymentIntents.default;

          const stripePromise = typeof window !== 'undefined' && window.Stripe
            ? Promise.resolve(window.Stripe(config.stripe.publishableKey))
            : Promise.reject(new Error('Stripe.js not loaded.'));

          return stripePromise.then(stripe => {
            return dispatch(
              confirmCardPayment({
                stripe,
                paymentParams: { payment_method: stripePaymentMethodId },
                stripePaymentIntentClientSecret,
                orderId: order.id,
              })
            ).then(result => ({
              order,
              transactionId: result.transactionId || order.id,
              paymentIntent: result.paymentIntent,
              process,
            }));
          });
        })
        .then(({ order, transactionId, process }) => {
          // ── Step 3: Confirm payment via Marketplace API ───────────────
          const confirmTransition = process.transitions.CONFIRM_PAYMENT;
          return dispatch(
            confirmPayment(transactionId, confirmTransition, {})
          ).then(() => order);
        })
        .then(order => {
          // ── Step 4: Update original 25% tx + listing ──────────────────
          const newTxId = order.id.uuid;

          // Update original transaction's protectedData (non-blocking)
          updateTransactionMetadata({
            transactionId: transaction.id.uuid,
            metadata: { restPaymentTxId: newTxId },
          }).catch(e => {
            log.error(e, 'initiate-rest-payment-update-metadata-failed', {
              txId: transaction.id.uuid,
              newTxId,
            });
          });

          // Mark listing as fully paid so Bill Reduction dropdown can exclude it
          updateListingForPartialPaymentTransaction({
            listingId: listing.id.uuid,
            publicData: { restPaymentDone: true },
          }).catch(e => {
            log.error(e, 'initiate-rest-payment-update-listing-failed', {
              listingId: listing.id.uuid,
              newTxId,
            });
          });

          // Refresh the current transaction entity in Redux store
          refreshTransactionEntity(sdk, transaction.id, dispatch);

          return { orderId: newTxId, originalTxId: transaction.id.uuid };
        });
    })
    .catch(e => {
      log.error(e, 'initiate-rest-payment-failed', {
        txId: transaction?.id?.uuid,
        listingId: listing?.id?.uuid,
      });
      return rejectWithValue(storableError(e));
    });
};

export const initiateRestPaymentThunk = createAsyncThunk(
  'TransactionPage/initiateRestPayment',
  initiateRestPaymentPayloadCreator
);

// Backward compatible wrapper for initiateRestPayment
export const initiateRestPayment = (transaction, listing, currentUser, config) => dispatch => {
  return dispatch(
    initiateRestPaymentThunk({ transaction, listing, currentUser, config })
  ).unwrap();
};

////////////////////
// Fetch Messages //
////////////////////
const fetchMessagesPayloadCreator = (
  { txId, page, config },
  { dispatch, rejectWithValue, extra: sdk }
) => {
  const paging = { page, perPage: MESSAGES_PAGE_SIZE };

  return sdk.messages
    .query({
      transaction_id: txId,
      include: ['sender', 'sender.profileImage'],
      ...getImageVariants(config.layout.listingImage),
      ...paging,
    })
    .then(response => {
      const messages = denormalisedResponseEntities(response);
      const { totalItems, totalPages, page: fetchedPage } = response.data.meta;
      const pagination = { totalItems, totalPages, page: fetchedPage };

      // Check if totalItems has changed between fetched pagination pages
      // if totalItems has changed, fetch first page again to include new incoming messages.
      // TODO if there're more than 100 incoming messages,
      // this should loop through most recent pages instead of fetching just the first one.
      if (totalItems > 0 && page > 1) {
        // Background update for new incoming messages
        dispatch(fetchMessagesThunk({ txId, page: 1, config })).catch(() => {
          // Background update, no need to to do anything atm.
        });
      }

      return { messages, pagination };
    })
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const fetchMessagesThunk = createAsyncThunk(
  'TransactionPage/fetchMessages',
  fetchMessagesPayloadCreator
);

// Backward compatible wrapper for fetchMessages
export const fetchMessages = (txId, page, config) => dispatch => {
  return dispatch(fetchMessagesThunk({ txId, page, config }));
};

const fetchMoreMessagesPayloadCreator = ({ txId, config }, { dispatch, getState }) => {
  const state = getState();
  const { oldestMessagePageFetched, totalMessagePages } = state.TransactionPage;
  const hasMoreOldMessages = totalMessagePages > oldestMessagePageFetched;

  // In case there're no more old pages left we default to fetching the current cursor position
  const nextPage = hasMoreOldMessages ? oldestMessagePageFetched + 1 : oldestMessagePageFetched;

  return dispatch(fetchMessagesThunk({ txId, page: nextPage, config }));
};

export const fetchMoreMessagesThunk = createAsyncThunk(
  'TransactionPage/fetchMoreMessages',
  fetchMoreMessagesPayloadCreator
);

// Backward compatible wrapper for fetchMoreMessages
export const fetchMoreMessages = (txId, config) => dispatch => {
  return dispatch(fetchMoreMessagesThunk({ txId, config }));
};

/////////////////
// sendMessage //
/////////////////
const sendMessagePayloadCreator = (
  { txId, message, config },
  { dispatch, rejectWithValue, extra: sdk }
) => {
  return sdk.messages
    .send({ transactionId: txId, content: message })
    .then(response => {
      const messageId = response.data.data.id;

      // We fetch the first page again to add sent message to the page data
      // and update possible incoming messages too.
      return dispatch(fetchMessagesThunk({ txId, page: 1, config }))
        .then(() => messageId)
        .catch(() => messageId);
    })
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const sendMessageThunk = createAsyncThunk(
  'TransactionPage/sendMessage',
  sendMessagePayloadCreator
);

// Backward compatible wrapper for sendMessage
export const sendMessage = (txId, message, config) => dispatch => {
  return dispatch(sendMessageThunk({ txId, message, config }));
};

////////////////
// sendReview //
////////////////
const sendReviewPayloadCreator = (
  { tx, transitionOptionsInfo, params, config },
  { dispatch, rejectWithValue, extra: sdk }
) => {
  const { reviewAsFirst, reviewAsSecond, hasOtherPartyReviewedFirst } = transitionOptionsInfo;
  const include = REVIEW_TX_INCLUDES;

  const sendReviewAsSecond = () => {
    return sdk.transactions
      .transition(
        { id: tx?.id, transition: reviewAsSecond, params },
        { expand: true, include, ...getImageVariants(config.layout.listingImage) }
      )
      .then(response => {
        dispatch(addMarketplaceEntities(response));
        return response;
      })
      .catch(e => {
        return rejectWithValue(storableError(e));
      });
  };

  const sendReviewAsFirst = () => {
    return sdk.transactions
      .transition(
        { id: tx?.id, transition: reviewAsFirst, params },
        { expand: true, include, ...getImageVariants(config.layout.listingImage) }
      )
      .then(response => {
        dispatch(addMarketplaceEntities(response));
        return response;
      })
      .catch(e => {
        // If transaction transition is invalid, lets try another endpoint.
        if (isTransactionsTransitionInvalidTransition(e)) {
          return sendReviewAsSecond();
        } else {
          return rejectWithValue(storableError(e));
        }
      });
  };

  return hasOtherPartyReviewedFirst ? sendReviewAsSecond() : sendReviewAsFirst();
};

export const sendReviewThunk = createAsyncThunk(
  'TransactionPage/sendReview',
  sendReviewPayloadCreator
);

// Backward compatible wrapper for sendReview
export const sendReview = (tx, transitionOptionsInfo, params, config) => dispatch => {
  return dispatch(sendReviewThunk({ tx, transitionOptionsInfo, params, config }));
};

//////////////////////
// fetchTransitions //
//////////////////////
const fetchTransitionsPayloadCreator = ({ id }, { rejectWithValue, extra: sdk }) => {
  return sdk.processTransitions
    .query({ transactionId: id })
    .then(res => res.data.data)
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const fetchTransitionsThunk = createAsyncThunk(
  'TransactionPage/fetchTransitions',
  fetchTransitionsPayloadCreator
);

// Backward compatible wrapper for fetchTransitions
export const fetchNextTransitions = id => dispatch => {
  return dispatch(fetchTransitionsThunk({ id }));
};

////////////////////
// FetchLineItems //
////////////////////
const fetchLineItemsPayloadCreator = (
  { orderData, listingId, isOwnListing },
  { rejectWithValue }
) => {
  return transactionLineItems({ orderData, listingId, isOwnListing })
    .then(response => {
      return response.data
    })
    .catch(e => {
      log.error(e, 'fetching-line-items-failed', {
        listingId: listingId.uuid,
        orderData,
        statusText: e.statusText,
      });
      return rejectWithValue(storableError(e));
    });
};

export const fetchLineItemsThunk = createAsyncThunk(
  'TransactionPage/fetchLineItems',
  fetchLineItemsPayloadCreator
);

// Backward compatible wrapper for fetchLineItems
export const fetchTransactionLineItems = ({ orderData, listingId, isOwnListing }) => dispatch => {
  return dispatch(fetchLineItemsThunk({ orderData, listingId, isOwnListing }));
};

// ================ Slice ================ //

const initialState = {
  fetchTransactionInProgress: false,
  fetchTransactionError: null,
  transactionRef: null,
  commission: null,
  fetchCommissionInProgress: false,
  fetchCommissionError: null,
  transitionInProgress: null,
  transitionError: null,
  fetchMessagesInProgress: false,
  fetchMessagesError: null,
  totalMessages: 0,
  totalMessagePages: 0,
  oldestMessagePageFetched: 0,
  messages: [],
  initialMessageFailedToTransaction: null,
  savePaymentMethodFailed: false,
  sendMessageInProgress: false,
  sendMessageError: null,
  sendReviewInProgress: false,
  sendReviewError: null,
  monthlyTimeSlots: {
    // '2022-03': {
    //   timeSlots: [],
    //   fetchTimeSlotsError: null,
    //   fetchTimeSlotsInProgress: null,
    // },
  },
  timeSlotsForDate: {
    // For small time units, we fetch monthly time slots with sparse mode for calendar view
    // and when the user clicks on a day, we make a full time slot query. This is for that purpose.
    // '2025-02-03': {
    //   timeSlots: [],
    //   fetchedAt: 1738569600000,
    //   fetchTimeSlotsError: null,
    //   fetchTimeSlotsInProgress: null,
    // },
  },
  fetchTransitionsInProgress: false,
  fetchTransitionsError: null,
  processTransitions: null,
  lineItems: null,
  fetchLineItemsInProgress: false,
  fetchLineItemsError: null,
  restPaymentInProgress: false,
  restPaymentError: null,
  restPaymentSuccess: false,
  restPaymentTxRef: null,
  invoiceTxRefs: [],
  inquiryListingDefaultImage: null,
  inquiryListingEntity: null,
};

// Merge entity arrays using ids, so that conflicting items in newer array (b) overwrite old values (a).
// const a = [{ id: { uuid: 1 } }, { id: { uuid: 3 } }];
// const b = [{ id: : { uuid: 2 } }, { id: : { uuid: 1 } }];
// mergeEntityArrays(a, b)
// => [{ id: { uuid: 3 } }, { id: : { uuid: 2 } }, { id: : { uuid: 1 } }]
const mergeEntityArrays = (a, b) => {
  return a.filter(aEntity => !b.find(bEntity => aEntity.id.uuid === bEntity.id.uuid)).concat(b);
};

const transactionPageSlice = createSlice({
  name: 'TransactionPage',
  initialState,
  reducers: {
    setInitialValues: (state, action) => {
      return { ...initialState, ...action.payload };
    },
    setInquiryListingDefaultImage: (state, action) => {
      state.inquiryListingDefaultImage = action.payload;
    },
    setInquiryListingEntity: (state, action) => {
      state.inquiryListingEntity = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      // fetchTransaction cases
      .addCase(fetchTransactionThunk.pending, state => {
        state.fetchTransactionInProgress = true;
        state.fetchTransactionError = null;
      })
      .addCase(fetchTransactionThunk.fulfilled, (state, action) => {
        state.fetchTransactionInProgress = false;
        state.transactionRef = { id: action.payload.data.data.id, type: 'transaction' };
        if (action.payload.restPaymentTxId) {
          state.restPaymentTxRef = { id: new UUID(action.payload.restPaymentTxId), type: 'transaction' };
        }
        if (Array.isArray(action.payload.invoiceTxIds) && action.payload.invoiceTxIds.length > 0) {
          state.invoiceTxRefs = action.payload.invoiceTxIds.map(id => ({ id: new UUID(id), type: 'transaction' }));
        }
      })
      .addCase(fetchTransactionThunk.rejected, (state, action) => {
        state.fetchTransactionInProgress = false;
        state.fetchTransactionError = action.payload;
      })
      // fetchCommission cases
      .addCase(fetchCommissionThunk.pending, state => {
        state.fetchCommissionInProgress = true;
        state.fetchCommissionError = null;
      })
      .addCase(fetchCommissionThunk.fulfilled, (state, action) => {
        state.fetchCommissionInProgress = false;
        state.commission = action.payload;
      })
      .addCase(fetchCommissionThunk.rejected, (state, action) => {
        state.fetchCommissionInProgress = false;
        state.fetchCommissionError = action.payload;
      })
      // fetchTransitions cases
      .addCase(fetchTransitionsThunk.pending, state => {
        state.fetchTransitionsInProgress = true;
        state.fetchTransitionsError = null;
      })
      .addCase(fetchTransitionsThunk.fulfilled, (state, action) => {
        state.fetchTransitionsInProgress = false;
        state.processTransitions = action.payload;
      })
      .addCase(fetchTransitionsThunk.rejected, (state, action) => {
        state.fetchTransitionsInProgress = false;
        state.fetchTransitionsError = action.payload;
      })
      // makeTransition cases
      .addCase(makeTransitionThunk.pending, (state, action) => {
        state.transitionInProgress = action.meta.arg.transitionName;
        state.transitionError = null;
      })
      .addCase(makeTransitionThunk.fulfilled, state => {
        state.transitionInProgress = null;
      })
      .addCase(makeTransitionThunk.rejected, (state, action) => {
        state.transitionInProgress = null;
        state.transitionError = action.payload;
      })
      // fetchMessages cases
      .addCase(fetchMessagesThunk.pending, state => {
        state.fetchMessagesInProgress = true;
        state.fetchMessagesError = null;
      })
      .addCase(fetchMessagesThunk.fulfilled, (state, action) => {
        const { messages, pagination } = action.payload;
        const oldestMessagePageFetched =
          state.oldestMessagePageFetched > pagination.page
            ? state.oldestMessagePageFetched
            : pagination.page;

        state.fetchMessagesInProgress = false;
        state.messages = mergeEntityArrays(state.messages, messages);
        state.totalMessages = pagination.totalItems;
        state.totalMessagePages = pagination.totalPages;
        state.oldestMessagePageFetched = oldestMessagePageFetched;
      })
      .addCase(fetchMessagesThunk.rejected, (state, action) => {
        state.fetchMessagesInProgress = false;
        state.fetchMessagesError = action.payload;
      })
      // sendMessage cases
      .addCase(sendMessageThunk.pending, state => {
        state.sendMessageInProgress = true;
        state.sendMessageError = null;
        state.initialMessageFailedToTransaction = null;
      })
      .addCase(sendMessageThunk.fulfilled, state => {
        state.sendMessageInProgress = false;
      })
      .addCase(sendMessageThunk.rejected, (state, action) => {
        state.sendMessageInProgress = false;
        state.sendMessageError = action.payload;
      })
      // sendReview cases
      .addCase(sendReviewThunk.pending, state => {
        state.sendReviewInProgress = true;
        state.sendReviewError = null;
      })
      .addCase(sendReviewThunk.fulfilled, state => {
        state.sendReviewInProgress = false;
        state.sendReviewError = null;
      })
      .addCase(sendReviewThunk.rejected, (state, action) => {
        state.sendReviewInProgress = false;
        state.sendReviewError = action.payload;
      })
      // fetchLineItems cases
      .addCase(fetchLineItemsThunk.pending, state => {
        state.fetchLineItemsInProgress = true;
        state.fetchLineItemsError = null;
      })
      .addCase(fetchLineItemsThunk.fulfilled, (state, action) => {
        state.fetchLineItemsInProgress = false;
        state.lineItems = action.payload;
      })
      .addCase(fetchLineItemsThunk.rejected, (state, action) => {
        state.fetchLineItemsInProgress = false;
        state.fetchLineItemsError = action.payload;
      })
      // fetchTimeSlots cases
      .addCase(fetchTimeSlotsThunk.pending, (state, action) => {
        const { timeZone, options } = action.meta.arg;
        const { useFetchTimeSlotsForDate = false } = options || {};

        if (useFetchTimeSlotsForDate) {
          const dateId = stringifyDateToISO8601(action.meta.arg.start, timeZone);
          state.timeSlotsForDate = {
            ...removeOutdatedDateData(state.timeSlotsForDate),
            [dateId]: {
              ...state.timeSlotsForDate[dateId],
              fetchTimeSlotsError: null,
              fetchedAt: null,
              fetchTimeSlotsInProgress: true,
              timeSlots: [],
            },
          };
        } else {
          const monthId = monthIdString(action.meta.arg.start, timeZone);
          state.monthlyTimeSlots[monthId] = {
            ...state.monthlyTimeSlots[monthId],
            fetchTimeSlotsError: null,
            fetchTimeSlotsInProgress: true,
          };
        }
      })
      .addCase(fetchTimeSlotsThunk.fulfilled, (state, action) => {
        const { timeSlots, dateId, monthId, type } = action.payload;

        if (type === 'date') {
          state.timeSlotsForDate[dateId] = {
            ...state.timeSlotsForDate[dateId],
            fetchTimeSlotsInProgress: false,
            fetchedAt: new Date().getTime(),
            timeSlots,
          };
        } else if (type === 'month') {
          state.monthlyTimeSlots[monthId] = {
            ...state.monthlyTimeSlots[monthId],
            fetchTimeSlotsInProgress: false,
            timeSlots,
          };
        }
      })
      .addCase(fetchTimeSlotsThunk.rejected, (state, action) => {
        const { error, dateId, monthId, type } = action.payload;

        if (type === 'date') {
          state.timeSlotsForDate[dateId] = {
            ...state.timeSlotsForDate[dateId],
            fetchTimeSlotsInProgress: false,
            fetchTimeSlotsError: error,
          };
        } else if (type === 'month') {
          state.monthlyTimeSlots[monthId] = {
            ...state.monthlyTimeSlots[monthId],
            fetchTimeSlotsInProgress: false,
            fetchTimeSlotsError: error,
          };
        }
      })
      // initiateRestPayment cases
      .addCase(initiateRestPaymentThunk.pending, state => {
        state.restPaymentInProgress = true;
        state.restPaymentError = null;
        state.restPaymentSuccess = false;
      })
      .addCase(initiateRestPaymentThunk.fulfilled, state => {
        state.restPaymentInProgress = false;
        state.restPaymentSuccess = true;
      })
      .addCase(initiateRestPaymentThunk.rejected, (state, action) => {
        console.error(action.payload);
        state.restPaymentInProgress = false;
        state.restPaymentError = action.payload;
      });
  },
});

export const {
  setInitialValues,
  setInquiryListingDefaultImage,
  setInquiryListingEntity,
} = transactionPageSlice.actions;

export default transactionPageSlice.reducer;

// ================ Load data ================ //

const isNonEmpty = value => {
  return typeof value === 'object' || Array.isArray(value) ? !isEmpty(value) : !!value;
};

// loadData is a collection of async calls that need to be made
// before page has all the info it needs to render itself
const loadDataPayloadCreator = ({ params, search, config }, { dispatch, getState }) => {
  const txId = new UUID(params.id);
  const state = getState().TransactionPage;
  const txRef = state.transactionRef;
  const txRole = params.transactionRole;

  // In case a transaction reference is found from a previous
  // data load -> clear the state. Otherwise keep the non-null
  // and non-empty values which may have been set from a previous page.
  const initialValues = txRef ? {} : pickBy(state, isNonEmpty);
  dispatch(setInitialValues(initialValues));

  const promisify = [dispatch(fetchTransactionThunk({ id: txId, txRole, config }))];

  if (txRole != 'admin') {
    promisify.push(
      ...[
        // dispatch(fetchCommission()),
        dispatch(fetchMessagesThunk({ txId, page: 1, config })),
        dispatch(fetchTransitionsThunk({ id: txId })),
      ]
    );
  }

  // Sale / order (i.e. transaction entity in API)
  return Promise.all(promisify);
};

export const loadDataThunk = createAsyncThunk('TransactionPage/loadData', loadDataPayloadCreator);

// Backward compatible wrapper for loadData
export const loadData = (params, search, config) => dispatch => {
  return dispatch(loadDataThunk({ params, search, config }));
};
